const { REACT_APP_NAME } = process.env;

import { useEffect, useMemo, useState, useRef } from "react"
import { useSearchParams } from "react-router-dom";
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from 'material-react-table';

import { useFinanceHistoryMutation } from "src/redux/api/cabinet";
import dayjs from "dayjs";
import { Typography, Box, Pagination } from "@mui/material";
import { NumberFormat } from "src/components/hooks/number-format/NumberFormat";

export const BalanceHistory = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        from: 1,
        to: 15
    });
    const tableRef = useRef<HTMLDivElement>(null);

    const [getData, { isLoading }] = useFinanceHistoryMutation();

    const getRequest = async (currentPage: number) => {
        const jwt = localStorage.getItem('jwt');
        await getData({
            jwt,
            page: currentPage,
            limit: 15
        })
            .unwrap()
            .then((payload: any) => {
                setPagination(payload.pagination);
                initDataTable(payload.data, payload.pagination);
            })
            .catch((error: any) => {
                console.error('Error fetching data:', error);
            })
    }

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setSearchParams({ page: value.toString() });
        getRequest(value);

        if (tableRef.current) {
            tableRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: "number",
                header: '№',
                size: 60,
                Cell: ({ row }) => (
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#64748b' }}>
                        {row.original.number}
                    </Typography>
                ),
            },
            {
                accessorFn: (row) => row.data.type_id,
                header: 'Тип',
                size: 180,
                Cell: ({ row }) => (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            bgcolor: '#f1f5f9',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#475569'
                        }}
                    >
                        {row.original.data.type_id}
                    </Box>
                ),
            },
            {
                accessorFn: (row) => row.data.created_at,
                header: 'Дата',
                size: 140,
                Cell: ({ row }) => (
                    <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#334155' }}>
                        {dayjs(row.original.data.created_at).format('DD.MM.YYYY HH:mm')}
                    </Typography>
                ),
            },
            {
                accessorFn: (row) => row.data.status_id.name,
                header: 'Статус',
                size: 130,
                Cell: ({ row }) => (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            bgcolor: `${row.original.data.status_id.color}15`,
                            border: `1px solid ${row.original.data.status_id.color}40`,
                            fontSize: '13px',
                            fontWeight: 600,
                            color: row.original.data.status_id.color
                        }}
                    >
                        {row.original.data.status_id.name}
                    </Box>
                ),
            },
            {
                accessorFn: (row) => row.data.name,
                header: 'Описание',
                size: 300,
                Cell: ({ row }) => (
                    <Typography
                        sx={{
                            fontWeight: 500,
                            fontSize: '14px',
                            color: '#1e293b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '300px'
                        }}
                        title={row.original.data.name}
                    >
                        {row.original.data.name}
                    </Typography>
                ),
            },
            {
                accessorFn: (row) => row.data.amount,
                header: 'Сумма',
                size: 120,
                Cell: ({ row }) => {
                    const currency = row.original.data.currency?.toLowerCase();
                    return (
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: '15px',
                                color: parseFloat(row.original.data.amount) > 0 ? '#10b981' : '#ef4444'
                            }}
                        >
                            <NumberFormat
                                number={parseFloat(row.original.data.amount) || 0}
                                symbol={currency === 'btc' ? 'BY' : undefined}
                            />
                        </Typography>
                    );
                },
            },
            {
                accessorFn: (row) => row.data.balance_null,
                header: 'Баланс NULL',
                size: 130,
                Cell: ({ row }) => {
                    const currency = row.original.data.currency?.toLowerCase();
                    return (
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#64748b' }}>
                            <NumberFormat
                                number={parseFloat(row.original.data.balance_null) || 0}
                                symbol={currency === 'btc' ? 'BY' : undefined}
                            />
                        </Typography>
                    );
                },
            },
            {
                accessorFn: (row) => row.data.balance,
                header: 'Баланс BY',
                size: 130,
                Cell: ({ row }) => {
                    const currency = row.original.data.currency?.toLowerCase();
                    return (
                        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>
                            <NumberFormat
                                number={parseFloat(row.original.data.balance_btc) || 0}
                                symbol={'BY'}
                            />
                        </Typography>
                    );
                },
            },
            {
                accessorFn: (row) => row.data.balance,
                header: 'Баланс',
                size: 130,
                Cell: ({ row }) => {
                    const currency = row.original.data.currency?.toLowerCase();
                    return (
                        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>
                            <NumberFormat
                                number={parseFloat(row.original.data.balance) || 0}
                            />
                        </Typography>
                    );
                },
            },
        ],
        [],
    );

    const table = useMaterialReactTable({
        columns,
        data,
        state: {
            isLoading,
        },
        enableStickyHeader: true,
        muiTablePaperProps: {
            elevation: 0,
            sx: {
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
            },
        },
        enableRowSelection: false,
        enableGlobalFilter: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableColumnFilters: false,
        enableColumnActions: false,
        enableSorting: false,
        enablePagination: false,
        muiTableBodyCellProps: {
            sx: {
                borderBottom: '1px solid #f1f5f9',
                py: 1.5,
                fontSize: '14px',
            },
        },
        muiTableBodyRowProps: {
            sx: {
                '&:hover': {
                    bgcolor: '#f8fafc',
                },
                transition: 'background-color 0.2s ease',
            },
        },
        muiTableProps: {
            sx: { boxShadow: "none" }
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: 'calc(100vh - 300px)',
                boxShadow: "none",
            }
        },
        muiTableHeadCellProps: {
            sx: {
                bgcolor: '#f8fafc',
                borderBottom: '2px solid #e2e8f0',
                fontSize: '12px',
                fontWeight: 700,
                color: '#475569',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                py: 1.5,
            },
        },
        localization: {
            and: 'и',
            cancel: 'Отмена',
            search: 'Искать',
        },
        renderEmptyRowsFallback: () => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                <Typography sx={{ fontSize: '16px', color: '#94a3b8', fontWeight: 500 }}>
                    Ничего не найдено
                </Typography>
            </Box>
        ),
    });

    const initDataTable = (anyData: any[], paginationData: any) => {
        const arrDataTable: any = [];
        anyData.forEach((e, i) => {
            arrDataTable.push({
                number: paginationData.from + i,
                data: e
            })
        });
        setData(arrDataTable);
    }

    useEffect(() => {
        const pageParam = searchParams.get('page');
        const currentPage = pageParam ? parseInt(pageParam, 10) : 1;

        if (isNaN(currentPage) || currentPage < 1) {
            setSearchParams({ page: '1' });
            getRequest(1);
        } else {
            getRequest(currentPage);
        }
    }, []);

    return (
        <Box ref={tableRef}>
            <MaterialReactTable table={table} />

            {pagination.last_page > 1 && (
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                        Показано {pagination.from} - {pagination.to} из {pagination.total} записей
                    </Typography>

                    <Pagination
                        count={pagination.last_page}
                        page={pagination.current_page}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        disabled={isLoading}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                fontSize: '14px',
                                fontWeight: 600,
                                minWidth: '36px',
                                height: '36px',
                                borderRadius: '6px',
                                mx: 0.25,
                                '&:hover': {
                                    bgcolor: '#f1f5f9',
                                },
                            },
                            '& .Mui-selected': {
                                bgcolor: '#3b82f6 !important',
                                color: '#ffffff',
                                fontWeight: 700,
                                '&:hover': {
                                    bgcolor: '#2563eb !important',
                                },
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    )
}